/**
 * Traps Tab focus inside a container while `active`, and restores focus to
 * whatever had it beforehand (the orb, or the chip) once it goes away.
 * Nothing like this exists yet anywhere in the codebase — the wardrobe's
 * CollectionSheet/SimilarSheet overlays don't trap focus at all — so this is
 * built from scratch for the MAHI chat panel, which is the first surface in
 * the app that behaves like a true modal.
 */
import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
  'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(', ');

export default function useFocusTrap(containerRef, { active, onEscape } = {}) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;
    const previouslyFocused = document.activeElement;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusables = Array.from(containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Focus returns to whatever opened the panel (the orb or the chip),
      // matching the accessibility spec: "returns to orb on close."
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}
